import { t } from 'i18next';
import { Vector3 } from 'three';
import { v4 as uuidv4 } from 'uuid';

import { PlacementUpdateEvent } from 'app/modules/wallforming/components/content-container/types/placementUpdateEvent';
import {
    isInputActionReleased,
    matchesInputAction
} from 'app/modules/wallforming/components/input-binding/hooks/useInputActionHandler';
import { InputActionId } from 'app/modules/wallforming/components/input-binding/types/inputActionId';
import { InputTrigger } from 'app/modules/wallforming/components/input-binding/types/inputTrigger';
import {
    chordFromMouseEvent,
    getEventModifiers,
    mouseCode,
    parseChord
} from 'app/modules/wallforming/components/input-binding/utils/inputChordUtils';
import {
    isSupportingConstruction,
    mapArticleInstanceToPlacement,
    mapPlacementToArticleInstance
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { isMultiPOISelection } from 'app/modules/wallforming/components/scenes/objects/articles/utils/clipboardUtils';
import { updateInstanceToPoiIndexMap } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { SelectionBox } from 'app/modules/wallforming/components/scenes/objects/helpers/SelectionBox';
import { getRelatedPoiIndices } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { MAIN_DIV_ID } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { GroupMember } from 'app/modules/wallforming/components/scenes/objects/articles/types/groupMember';
import type { BoxSelectionResult } from 'app/modules/wallforming/components/scenes/objects/helpers/types/boxSelectionResult';
import type { SceneEventHandlerSliceState } from 'app/modules/wallforming/components/state-management/placement/states/sceneEventHandlerSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

let isBoxSelectionActiveFlag = false;
let boxSelectionButton = 0;

const ndcPosition = new Vector3();

const boxSelectionStartThreshold = 5;
let boxSelectionStartClientX = 0;
let boxSelectionStartClientY = 0;

let animationFrameId: number | null = null;

const boxSelectionActions = [
    InputActionId.SelectionBoxRecursive,
    InputActionId.SelectionBoxFullyEnclosed,
    InputActionId.SelectionBoxPartiallyEnclosed
] as const;

const boxSelectionChord = (event: PointerEvent, button: number) =>
    chordFromMouseEvent(event, InputTrigger.Drag, button);

const matchesBoxSelection = (event: PointerEvent, state: PlacementState, button: number = event.button) =>
    boxSelectionActions
        .slice(1)
        .some((actionId) => matchesInputAction(actionId, boxSelectionChord(event, button), state)) ||
    (matchesRecursiveSelection(event, state) &&
        boxSelectionActions.slice(1).some((actionId) =>
            state.inputBindings.slots[actionId].some((chord) => {
                if (!chord || !matchesInputAction(actionId, chord, state)) {
                    return false;
                }
                const parsed = parseChord(chord);
                return parsed?.code === mouseCode(button) && parsed.trigger === InputTrigger.Drag;
            })
        ));

const matchesRecursiveSelection = (event: PointerEvent, state: PlacementState) => {
    const eventModifiers = getEventModifiers(event);
    return state.inputBindings.slots[InputActionId.SelectionBoxRecursive].some((chord) => {
        if (!chord) {
            return false;
        }
        const parsed = parseChord(chord);
        return Boolean(
            parsed?.trigger === InputTrigger.Drag &&
            parsed.modifiers.length > 0 &&
            parsed.modifiers.length === eventModifiers.length &&
            parsed.modifiers.every((modifier) => eventModifiers.includes(modifier))
        );
    });
};

/** Modifiers are re-evaluated on every event, so they can be changed while dragging. */
function resolveBoxSelectionAction(event: PointerEvent, state: PlacementState) {
    const chord = boxSelectionChord(event, boxSelectionButton);
    const isLeftToRight = event.clientX >= boxSelectionStartClientX;
    return boxSelectionActions.find(
        (actionId) =>
            matchesInputAction(actionId, chord, state) &&
            (actionId === InputActionId.SelectionBoxRecursive
                ? matchesRecursiveSelection(event, state)
                : isLeftToRight === (actionId === InputActionId.SelectionBoxFullyEnclosed))
    );
}

function computeNDCPosition(x: number, y: number, domElement: HTMLCanvasElement): Vector3 {
    const rect = domElement.getBoundingClientRect();
    ndcPosition.set(((x - rect.left) / rect.width) * 2 - 1, -((y - rect.top) / rect.height) * 2 + 1, 0);
    return ndcPosition;
}

function applyBoxSelection(state: PlacementState, isRecursiveSelection: boolean): void {
    if (!state.selectionBox || !state.mainWebGLRendererRef.current || !state.mainCameraControlsRef.current) {
        return;
    }
    state.syncMainCameras(state.mainWebGLRendererRef.current.domElement);
    state.selectionBox.select();

    const boxSelectionResult: BoxSelectionResult = {};

    if (state.isBatchModeActive()) {
        const batchUUIDToMap: Record<string, Map<number, string>> = {};
        const { physical: physicalBatch, sheet: sheetBatch, holes: holesBatch } = state.batchedMeshes;

        if (physicalBatch) {
            batchUUIDToMap[physicalBatch.uuid] = state.physicalBatchToInstance;
        }
        if (sheetBatch) {
            batchUUIDToMap[sheetBatch.uuid] = state.sheetBatchToInstance;
        }
        if (holesBatch) {
            batchUUIDToMap[holesBatch.uuid] = state.holesBatchToInstance;
        }

        let poiRelatedIds: Set<string> | null = null;

        if (!isRecursiveSelection) {
            const poiIndex = state.poiSelection.current.index;
            if (poiIndex === undefined) {
                return;
            }
            const selectedPOI = state.pois[poiIndex];
            if (!selectedPOI) {
                return;
            }

            poiRelatedIds = new Set<string>(Object.values(selectedPOI.articles).flat());
        }

        Object.entries(state.selectionBox.batches).forEach(([batchUUID, batchIds]) => {
            const reverseMap = batchUUIDToMap[batchUUID];
            if (!reverseMap || batchIds.length === 0) {
                return;
            }

            // Pre-compute instance-key-to-index maps to avoid repeated Object.keys + indexOf
            const articleIndexCache: Record<string, Map<string, number>> = {};

            batchIds.forEach((batchId: number) => {
                const articleInstanceId = reverseMap.get(batchId);
                if (!articleInstanceId || (!isRecursiveSelection && !poiRelatedIds?.has(articleInstanceId))) {
                    return;
                }

                const batchEntry = state.instanceBatchIds.get(articleInstanceId);
                if (!batchEntry) {
                    return;
                }

                const articleId = batchEntry.articleId;

                if (!boxSelectionResult[articleInstanceId]) {
                    let indexMap = articleIndexCache[articleId];
                    if (!indexMap) {
                        indexMap = new Map<string, number>();
                        const keys = Object.keys(state.articles[articleId].articleInstances);
                        for (let i = 0; i < keys.length; i++) {
                            indexMap.set(keys[i], i);
                        }
                        articleIndexCache[articleId] = indexMap;
                    }
                    const index = indexMap.get(articleInstanceId) ?? 0;

                    boxSelectionResult[articleInstanceId] = {
                        articleId,
                        index,
                        meshesCount: 1
                    };
                } else {
                    boxSelectionResult[articleInstanceId].meshesCount += 1;
                }
            });
        });
    }

    let instanceSelections: Record<string, number | undefined> = {};

    if (state.selectionBox.isLeftToRight) {
        Object.entries(boxSelectionResult).forEach(([articleInstanceId, resultData]) => {
            const batchEntry = state.instanceBatchIds.get(articleInstanceId);
            const expectedCount = batchEntry
                ? batchEntry.physicalIds.length + (batchEntry.sheetId !== null ? 1 : 0) + batchEntry.holesIds.length
                : state.articles[resultData.articleId].meshesCount;

            if (resultData.meshesCount === expectedCount) {
                instanceSelections[articleInstanceId] = resultData.index;
            }
        });
    } else {
        instanceSelections = Object.fromEntries(
            Object.entries(boxSelectionResult).map(([articleInstanceId, resultData]) => [
                articleInstanceId,
                resultData.index
            ])
        );
    }

    // Expand selection to include all group members
    const expandedSelections = state.handleGroupSelections(Object.keys(instanceSelections));
    // Merge the expanded selections with the original ones to preserve indices
    for (const [id, idx] of Object.entries(instanceSelections)) {
        if (idx !== undefined) {
            expandedSelections[id] = idx;
        }
    }

    state.setArticleInstanceSelections(expandedSelections);
}

export function isBoxSelectionActive(): boolean {
    return isBoxSelectionActiveFlag;
}

export const createSceneEventHandlerSlice: StateCreator<PlacementState, [], [], SceneEventHandlerSliceState> = (
    _set,
    get
) => ({
    handleOnWindowPointerUp: (event) => {
        const manualInsertionButton = get().manualArticleInsertionButtonRef.current;
        if (manualInsertionButton === event.button) {
            if (get().isDragNewArticleActiveRef.current || get().isDragActiveRef.current) {
                get().handleOnDragArticleEnd();
            } else {
                get().cancelManualArticleInsertionDrag();
            }
            if (event.button !== 0) {
                return;
            }
        }

        if (event.button !== 0 || get().isAutoDragActiveRef.current) {
            return;
        }

        const mainWebGLRendererRef = get().mainWebGLRendererRef;
        if (mainWebGLRendererRef.current && mainWebGLRendererRef.current.domElement.style.cursor !== 'auto') {
            mainWebGLRendererRef.current.domElement.style.cursor = 'auto';
        }

        if (get().isDraggingActive()) {
            get().handleOnDragArticleEnd();
        }

        if (get().isDragNewArticleStartRef.current) {
            get().setIsDragNewArticleStartRef(false);

            if (get().poiSelection.current.filterType === undefined && event.offsetX >= 500) {
                toastNotifications.information({ title: t('info'), message: t('infoSelectPOI') });
            }
        }

        if (get().isArticleDirectlySelectedRef.current) {
            get().setIsArticleDirectlySelectedRef(false);
        }
    },

    handleOnWindowPointerDown: (event) => {
        if (
            event.button !== 0 ||
            !get().isAutoDragActiveRef.current ||
            get().interactionMode === InteractionMode.POI_NAVIGATION
        ) {
            return;
        }

        if ((event.target as HTMLCanvasElement)?.offsetParent?.id !== MAIN_DIV_ID) {
            return;
        }

        // Insert copy of placement indicator per pointer down event
        if (get().isAutoDragActiveRef.current) {
            const state = get();

            const selectedPoiIndex = state.poiSelection.current.index;
            const selectedPoiGuid =
                selectedPoiIndex !== undefined ? state.pois[selectedPoiIndex]?.poi.poiGuid : undefined;
            if (!selectedPoiGuid || selectedPoiIndex === undefined) {
                toastNotifications.information({ title: t('info'), message: t('infoSelectPOI') });
                return;
            }

            const selectionKeys = Object.keys(state.articleInstanceSelections);
            const selectedIds = new Set(selectionKeys);
            if (selectedIds.size === 0) {
                return;
            }

            const isMultiAlignmentInsertion = isMultiPOISelection(state.clipboard);
            if (isMultiAlignmentInsertion) {
                updateInstanceToPoiIndexMap(selectionKeys, state);
            }

            const updatedInstances: Array<{
                articleId: string;
                instanceId: string;
                instance: ReturnType<typeof mapPlacementToArticleInstance>;
            }> = [];
            const pastedGroupMembers = new Map<
                string,
                {
                    name: string;
                    members: GroupMember[];
                }
            >();

            for (const [articleId, dragRef] of state.articleDragRefsMap.current) {
                const instances = dragRef.articleRef.current?.articleInstances;
                if (!instances) {
                    continue;
                }

                for (const [instanceId, instanceData] of Object.entries(instances)) {
                    if (!selectedIds.has(instanceId)) {
                        continue;
                    }

                    const newInstanceId = uuidv4();

                    const sourceGroupId = state.instanceToGroupMap[instanceId];
                    if (sourceGroupId) {
                        const sourceGroup = state.articleGroups[sourceGroupId];
                        if (sourceGroup) {
                            const existing = pastedGroupMembers.get(sourceGroupId) ?? {
                                name: sourceGroup.name,
                                members: []
                            };
                            existing.members.push({
                                instanceId: newInstanceId,
                                articleId
                            });
                            pastedGroupMembers.set(sourceGroupId, existing);
                        }
                    }

                    const poiGuids = isMultiAlignmentInsertion
                        ? instanceData.poiGuids?.filter(Boolean)
                        : getRelatedPoiIndices(state.pois, selectedPoiIndex)
                              .map((poiIndex) => state.pois[poiIndex]?.poi.poiGuid)
                              .filter((poiGuid): poiGuid is string => Boolean(poiGuid));
                    const resolvedPoiGuids = poiGuids?.length ? poiGuids : [selectedPoiGuid];

                    const placement = mapArticleInstanceToPlacement(
                        newInstanceId,
                        instanceData,
                        resolvedPoiGuids,
                        true
                    );
                    updatedInstances.push({
                        articleId,
                        instanceId: newInstanceId,
                        instance: mapPlacementToArticleInstance(placement)
                    });
                }
            }

            if (updatedInstances.length === 0) {
                return;
            }

            requestAnimationFrame(() => {
                usePlacementStore.setState({
                    placementUpdate: {
                        event: PlacementUpdateEvent.PLACE,
                        articleInstanceIds: new Set(updatedInstances.map(({ instanceId }) => instanceId))
                    }
                });
            });

            state.updateArticleInstances(updatedInstances, true);

            requestAnimationFrame(() => {
                state.setLastSelectionCache(
                    Object.fromEntries(updatedInstances.map(({ instanceId }) => [instanceId, undefined])) ?? {}
                );
            });

            // Re-create groups
            for (const [, groupData] of pastedGroupMembers) {
                if (groupData.members.length >= 2) {
                    state.createArticleGroup(groupData.name, groupData.members);
                }
            }

            if (state.autoPlaceSupportingConstructionClamps) {
                for (const { articleId, instanceId } of updatedInstances) {
                    if (isSupportingConstruction(state.articles[articleId]?.articleNumber ?? null)) {
                        void state.reconcileSupportingConstructionClamps?.(instanceId);
                    }
                }
            }

            return;
        }
    },

    handleOnWindowKeyUp: (keyEvent) => {
        if (get().isSpaceKeyPressed && isInputActionReleased(InputActionId.PoiNavigationTemporary, keyEvent, get())) {
            get().setIsSpaceKeyPressed(false);
            get().setInteractionMode(InteractionMode.DEFAULT);
        }
    },

    handleBoxSelectionStart: (event) => {
        if (
            !matchesBoxSelection(event, get()) ||
            get().isDragActiveRef.current ||
            get().isAutoDragActiveRef.current ||
            get().isArticleDirectlySelectedRef.current ||
            get().isTransformationActiveRef.current
        ) {
            return;
        }

        if ((event.target as HTMLCanvasElement)?.offsetParent?.id !== MAIN_DIV_ID) {
            return;
        }

        const state = get();
        if (state.selectionBox && state.mainWebGLRendererRef.current) {
            state.selectionBox.batches = {};

            const start = computeNDCPosition(
                event.clientX,
                event.clientY,
                state.mainWebGLRendererRef.current!.domElement
            );
            if (start) {
                state.selectionBox.startPoint.copy(start);
                isBoxSelectionActiveFlag = true;
                boxSelectionButton = event.button;
                boxSelectionStartClientX = event.clientX;
                boxSelectionStartClientY = event.clientY;
            }
        }

        if (state.isAutoDragActiveRef.current) {
            return;
        }

        state.discardArticleInstanceSelection(false);
    },

    handleBoxSelection: (event) => {
        if (!isBoxSelectionActiveFlag || get().isDraggingActive()) {
            return;
        }

        const state = get();
        if (!state.selectionBox || !state.mainWebGLRendererRef.current) {
            return;
        }

        const end = computeNDCPosition(event.clientX, event.clientY, state.mainWebGLRendererRef.current.domElement);
        if (end) {
            state.selectionBox.endPoint.copy(end);
        }

        // Handle selection: throttle selection only once per animation frame - not for every pointer move
        if (isBoxSelectionActiveFlag && animationFrameId === null) {
            const dx = event.clientX - boxSelectionStartClientX;
            const dy = event.clientY - boxSelectionStartClientY;
            const actionId = resolveBoxSelectionAction(event, state);
            if (actionId && dx * dx + dy * dy >= boxSelectionStartThreshold * boxSelectionStartThreshold) {
                const isRecursive = actionId === InputActionId.SelectionBoxRecursive;
                animationFrameId = requestAnimationFrame(() => {
                    animationFrameId = null;
                    applyBoxSelection(get(), isRecursive);
                });
            }
        }
    },

    handleBoxSelectionEnd: (event) => {
        const state = get();
        if (!isBoxSelectionActiveFlag || !state.selectionBox) {
            return;
        }

        // Cancel any pending selection
        if (animationFrameId !== null) {
            cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
        }

        // check whether the pointer was pressed between pointer down and up events to avoid triggering box selection by simple scene clicks
        const dx = event.clientX - boxSelectionStartClientX;
        const dy = event.clientY - boxSelectionStartClientY;
        if (Math.sqrt(dx * dx + dy * dy) < boxSelectionStartThreshold) {
            isBoxSelectionActiveFlag = false;
            return;
        }

        if (!state.mainCameraControlsRef.current?.camera || !state.mainWebGLRendererRef.current) {
            return;
        }

        const end = computeNDCPosition(event.clientX, event.clientY, state.mainWebGLRendererRef.current.domElement);
        if (!end || state.selectionBox.startPoint.equals(end)) {
            isBoxSelectionActiveFlag = false;
            return;
        }

        state.selectionBox.endPoint.copy(end);
        isBoxSelectionActiveFlag = false;
        const actionId = resolveBoxSelectionAction(event, state);
        if (actionId) {
            applyBoxSelection(state, actionId === InputActionId.SelectionBoxRecursive);
        }
    },

    initializeSelectionBox: (scene) => {
        const state = get();
        if (!state.mainCameraControlsRef.current || !scene || !state.mainWebGLRendererRef.current) {
            return;
        }

        const camera = state.mainCameraControlsRef.current.camera;
        state.selectionBox = new SelectionBox(camera, scene);
    },

    isSelectionHelperEnabled: (event) => {
        const state = get();
        return (
            !state.isArticleDirectlySelectedRef.current &&
            !state.isDragActiveRef.current &&
            !state.isDragNewArticleActiveRef.current &&
            !state.isDragNewArticleStartRef.current &&
            !state.isAutoDragActiveRef.current &&
            !state.isTransformationActiveRef.current &&
            (isBoxSelectionActiveFlag || matchesBoxSelection(event, state))
        );
    }
});
